// src/services/emailService.ts
//
// Thin wrapper around the send-transactional-email edge function.
// Call sites use these helpers — never the edge function directly — so we can
// evolve templates, routing, and rate limits in one place.

import { supabase } from '../supabase/config';
import { loadLetterReleasedTemplate, hrDiscussFlagTemplate } from './emailTemplates';

// Fallback stakeholder — saved in memory as the HR & People exec who receives "discuss" flags.
const HR_DISCUSS_RECIPIENT = 'AFranklin@scr.vic.edu.au';
const HR_DISCUSS_RECIPIENT_FIRST_NAME = 'Alison';

function resolveAppUrl(): string {
  // Prefer an explicit env-configured URL (set per deployment). Falls back to
  // the browser's origin, which works in all practical cases since these
  // helpers are invoked from the running app.
  const envUrl = (import.meta as any)?.env?.VITE_APP_URL as string | undefined;
  if (envUrl) return envUrl;
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin;
  return 'https://pit.scr.vic.edu.au';
}

interface EmailInvocation {
  to: string;
  subject: string;
  html: string;
  text?: string;
  event_type: string;
  target_id?: string;
  replyTo?: string;
}

interface EmailResult {
  to: string;
  event_type: string;
  target_id?: string;
  status: 'sent' | 'failed' | 'skipped_duplicate' | 'skipped_no_api_key' | 'skipped_disabled';
  provider_id?: string;
  error?: string;
}

async function invokeEdgeFunction(requests: EmailInvocation[]): Promise<EmailResult[]> {
  try {
    const { data, error } = await supabase.functions.invoke('send-transactional-email', {
      body: { emails: requests }
    });
    if (error) {
      console.error('emailService: edge function invoke failed', error);
      return requests.map(r => ({
        to: r.to,
        event_type: r.event_type,
        target_id: r.target_id,
        status: 'failed',
        error: error.message
      }));
    }
    return (data as { results: EmailResult[] })?.results || [];
  } catch (err) {
    console.error('emailService: unexpected error', err);
    return requests.map(r => ({
      to: r.to,
      event_type: r.event_type,
      target_id: r.target_id,
      status: 'failed',
      error: (err as Error)?.message ?? 'unknown'
    }));
  }
}

export const emailService = {
  /**
   * Fire-and-forget wrapper. Errors are swallowed (logged to console) so that
   * email failures never block the primary action that triggered them
   * (e.g. releasing a load letter should succeed even if the email bounces).
   * Returns a Promise that resolves with EmailResult[] for call sites that
   * want to inspect outcomes.
   */
  async send(requests: EmailInvocation[]): Promise<EmailResult[]> {
    if (requests.length === 0) return [];
    return invokeEdgeFunction(requests);
  },

  /**
   * Send "Your load letter has been released" to a batch of teachers.
   * Idempotent — edge function dedupes on (event_type, target_id, recipient).
   */
  async sendLoadLetterReleased(
    letters: Array<{
      letterId: string;
      teacherEmail: string;
      teacherFirstName: string;
      semesterLabel: string;
    }>
  ): Promise<EmailResult[]> {
    const appUrl = resolveAppUrl();
    const emails: EmailInvocation[] = letters
      .filter(l => !!l.teacherEmail)
      .map(l => {
        const tpl = loadLetterReleasedTemplate({
          teacherFirstName: l.teacherFirstName || 'there',
          semesterLabel: l.semesterLabel,
          appUrl
        });
        return {
          to: l.teacherEmail,
          subject: tpl.subject,
          html: tpl.html,
          text: tpl.text,
          event_type: 'load_letter_released',
          target_id: l.letterId
        };
      });
    return this.send(emails);
  },

  /**
   * Send "Teacher wants to discuss their FTE / day-off preferences" to the HR leader.
   * Idempotent per intention.
   */
  async sendHrDiscussFlag(params: {
    intentionId: string;
    teacherName: string;
    teacherStaffCode: string;
    department: string;
    year: number;
    reasonSummary: string;    // e.g. "FTE / load"
  }): Promise<EmailResult[]> {
    const appUrl = resolveAppUrl();
    const tpl = hrDiscussFlagTemplate({
      recipientFirstName: HR_DISCUSS_RECIPIENT_FIRST_NAME,
      teacherName: params.teacherName,
      teacherStaffCode: params.teacherStaffCode,
      department: params.department,
      year: params.year,
      reasonSummary: params.reasonSummary,
      appUrl,
      intentionId: params.intentionId
    });
    return this.send([
      {
        to: HR_DISCUSS_RECIPIENT,
        subject: tpl.subject,
        html: tpl.html,
        text: tpl.text,
        event_type: 'hr_discuss_flag',
        target_id: params.intentionId
      }
    ]);
  }
};
